/**
 * Les succès de STEVE INVADERS — la logique, sans DOM ni React.
 *
 * Un succès se décide en comparant deux images consécutives de la partie :
 * `unlockedBy(avant, après)` rend la liste de ceux qui viennent de tomber. Rien
 * n'est déduit d'un état seul, parce que « le score vient de passer mille » et
 * « le score est au-dessus de mille » ne sont pas la même chose — la seconde
 * lecture rejouerait la notification à chaque image.
 *
 * Tout ce dont ces règles ont besoin est déjà dans `GameState` : les compteurs
 * de partie et de vague, et le bilan de fin de vague. Aucun état parallèle à
 * tenir à jour, donc aucun moyen que les deux divergent.
 */

import { S } from '../../i18n/strings';
import type { Leaf } from '../../i18n/strings';
import { readString, writeString } from '../../lib/storage';
import {
  BIG_BUBBLE,
  EIGHT,
  FOE_DOWN,
  FOUR,
  HEART,
  HEART_CRACKED,
  STAR,
  TARGET,
} from './badges';
import type { GameState } from './game';
import type { Sprite } from './sprites';

/**
 * Les identifiants, dans l'ordre d'affichage de la liste : du plus courant au
 * plus rare, comme un tableau Steam.
 */
export const ACHIEVEMENTS = [
  'first',
  'clean_wave',
  'sharpshooter',
  'last_heart',
  'wave_four',
  'thousand',
  'wave_eight',
  'pacifist',
] as const;

export type AchievementId = (typeof ACHIEVEMENTS)[number];

export interface Achievement {
  readonly id: AchievementId;
  readonly badge: Sprite;
  readonly name: Leaf;
  readonly line: Leaf;
}

export const ACHIEVEMENT_INFO: Record<AchievementId, Achievement> = {
  first: { id: 'first', badge: FOE_DOWN, name: S.pixel.awards.first.name, line: S.pixel.awards.first.line },
  clean_wave: {
    id: 'clean_wave',
    badge: HEART,
    name: S.pixel.awards.clean_wave.name,
    line: S.pixel.awards.clean_wave.line,
  },
  sharpshooter: {
    id: 'sharpshooter',
    badge: TARGET,
    name: S.pixel.awards.sharpshooter.name,
    line: S.pixel.awards.sharpshooter.line,
  },
  last_heart: {
    id: 'last_heart',
    badge: HEART_CRACKED,
    name: S.pixel.awards.last_heart.name,
    line: S.pixel.awards.last_heart.line,
  },
  wave_four: {
    id: 'wave_four',
    badge: FOUR,
    name: S.pixel.awards.wave_four.name,
    line: S.pixel.awards.wave_four.line,
  },
  thousand: {
    id: 'thousand',
    badge: STAR,
    name: S.pixel.awards.thousand.name,
    line: S.pixel.awards.thousand.line,
  },
  wave_eight: {
    id: 'wave_eight',
    badge: EIGHT,
    name: S.pixel.awards.wave_eight.name,
    line: S.pixel.awards.wave_eight.line,
  },
  pacifist: {
    id: 'pacifist',
    badge: BIG_BUBBLE,
    name: S.pixel.awards.pacifist.name,
    line: S.pixel.awards.pacifist.line,
  },
};

/** Seuil du succès de score. */
const SCORE_MARK = 1000;

/**
 * Les succès qui viennent de tomber entre ces deux images.
 *
 * La comparaison des bilans de vague se fait par **identité** : `stepGame`
 * fabrique un objet neuf à chaque fin de vague, et rend l'état tel quel quand
 * la partie ne tourne pas. Deux images qui partagent le même bilan sont donc
 * la même fin de vague, déjà comptée.
 */
export function unlockedBy(previous: GameState, next: GameState): AchievementId[] {
  const won: AchievementId[] = [];

  if (previous.kills === 0 && next.kills > 0) won.push('first');
  if (previous.score < SCORE_MARK && next.score >= SCORE_MARK) won.push('thousand');
  if (previous.phase === 'playing' && next.phase === 'over' && next.shots === 0) {
    won.push('pacifist');
  }

  const cleared = next.clearedWave;
  if (cleared !== null && cleared !== previous.clearedWave) {
    if (cleared.lostLives === 0) won.push('clean_wave');
    // Chaque bulle a touché : il n'en reste aucune en l'air, aucune perdue.
    if (cleared.hits > 0 && cleared.shots === cleared.hits) won.push('sharpshooter');
    if (cleared.livesLeft === 1) won.push('last_heart');
    if (cleared.wave === 4) won.push('wave_four');
    if (cleared.wave === 8) won.push('wave_eight');
  }

  return won;
}

export function isAchievementId(value: unknown): value is AchievementId {
  return typeof value === 'string' && (ACHIEVEMENTS as readonly string[]).includes(value);
}

/** Clé de stockage : les succès survivent au rechargement, comme le record. */
export const AWARDS_KEY = 'steve.pixel.succes';

/**
 * Longueur maximale acceptée à la lecture. Elle est calculée à partir des
 * identifiants réels, pour qu'un succès ajouté plus tard ne fasse pas
 * silencieusement rejeter la sauvegarde de tout le monde.
 */
const AWARDS_MAX_LENGTH = ACHIEVEMENTS.join(',').length;

/**
 * Relit la liste sauvegardée. Tout ce qui sort du stockage est traité comme
 * hostile : on ne garde que des identifiants connus, une seule fois chacun, et
 * on les remet dans l'ordre officiel — un fichier trafiqué ne peut donc ni
 * inventer un succès, ni désordonner la liste, ni la faire grandir.
 */
export function readAwards(): AchievementId[] {
  const raw = readString(AWARDS_KEY, AWARDS_MAX_LENGTH);
  if (raw === null || raw === '') return [];
  const found = new Set(raw.split(',').filter(isAchievementId));
  return ACHIEVEMENTS.filter((id) => found.has(id));
}

export function writeAwards(ids: readonly AchievementId[]): void {
  const kept = ACHIEVEMENTS.filter((id) => ids.includes(id));
  writeString(AWARDS_KEY, kept.join(','));
}
